import { useEffect, useState, useRef, useCallback } from 'react';
import { dashboardService } from '../services';
import { getGlobalAgence } from './useGlobalAgence';

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

// Agrège les compteurs métier visibles dans la navbar / le menu :
// - échéances proches (contrats arrivant à échéance ce mois)
// - impayés (contrats avec resteAPayer > 0)
// - sinistres à instruire (déclarés ou en cours)
//
// Polling intelligent :
// - poll toutes les 60s tant que le tab est visible
// - pause si document.visibilityState === 'hidden'
// - resume + refresh immédiat au retour de focus
//
// Retourne : { counts, loading, refresh }
// counts = { echeances, impayes, sinistresEnAttente, total, fetchedAt }
const useNotifications = ({ onNewActivity } = {}) => {
    const [counts, setCounts] = useState({
        echeances: 0,
        impayes: 0,
        sinistresEnAttente: 0,
        total: 0,
        fetchedAt: null,
    });
    const [loading, setLoading] = useState(true);
    const previousRef = useRef(null);
    const timerRef = useRef(null);

    const fetchCounts = useCallback(async () => {
        try {
            const ag = getGlobalAgence();
            const params = ag && ag !== 'all' ? { agence: ag } : {};
            const res = await dashboardService.getStats(params);
            const stats = res?.data || res || {};
            const next = {
                echeances: stats.echeancesProchaines || stats.echeances || 0,
                impayes: stats.nbImpayes || stats.impayes || 0,
                sinistresEnAttente: stats.sinistresEnCours || stats.sinistresEnAttente || 0,
                fetchedAt: Date.now(),
            };
            next.total = next.echeances + next.impayes + next.sinistresEnAttente;

            // Détection d'activité nouvelle (au-delà du premier load)
            const prev = previousRef.current;
            if (prev && onNewActivity) {
                const deltaSinistres = next.sinistresEnAttente - prev.sinistresEnAttente;
                const deltaImpayes = next.impayes - prev.impayes;
                if (deltaSinistres > 0 || deltaImpayes > 0) {
                    onNewActivity({ deltaSinistres: Math.max(0, deltaSinistres), deltaImpayes: Math.max(0, deltaImpayes), next });
                }
            }
            previousRef.current = next;
            setCounts(next);
        } catch {
            // Pas d'erreur visible — c'est du polling silencieux
        } finally {
            setLoading(false);
        }
    }, [onNewActivity]);

    const schedule = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (document.visibilityState === 'hidden') return; // pas de poll en arrière-plan
        timerRef.current = setTimeout(() => {
            fetchCounts().finally(schedule);
        }, POLL_INTERVAL_MS);
    }, [fetchCounts]);

    useEffect(() => {
        fetchCounts().finally(schedule);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchCounts().finally(schedule);
            } else if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
        const onAgenceChange = () => { fetchCounts(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('cap:agence-change', onAgenceChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('cap:agence-change', onAgenceChange);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { counts, loading, refresh: fetchCounts };
};

export default useNotifications;
